import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { feedbackKeys, FEEDBACK_GUIDE } from '../../api/feedback';
import { Button } from '../../components/Button';
import { ErrorBanner } from '../../components/ErrorBanner';
import { fieldStyles } from '../../components/styles';
import { toast } from '../../components/Toast';
import { useT } from '../../i18n/provider';
import { useSubmit } from '../auth/useSubmit';

export function FeedbackKeyPanel({ companyId }: { companyId: string }) {
  const t = useT();
  const key = useQuery({ queryKey: ['feedback-key', companyId], queryFn: () => feedbackKeys.get(companyId), enabled: companyId !== '' });
  const [secret, setSecret] = useState('');
  const [rotating, setRotating] = useState(false);
  const { submit, busy, error } = useSubmit<void>();
  useEffect(() => {
    const hide = () => { if (document.hidden) setSecret(''); };
    document.addEventListener('visibilitychange', hide);
    return () => document.removeEventListener('visibilitychange', hide);
  }, []);
  return <section className="space-y-4 rounded-lg border border-line bg-raised p-5">
    <div><h3 className="font-semibold text-ink">{t('feedback.key.title')}</h3><p className="mt-1 max-w-3xl text-sm text-ink-soft">{t('feedback.key.hint')}</p></div>
    <a className="text-sm text-primary underline" href={FEEDBACK_GUIDE} target="_blank" rel="noreferrer">{t('feedback.guide')}</a>
    {key.error || error ? <ErrorBanner error={error ?? key.error} /> : null}
    {key.data?.configured ? <>
      <p className="text-sm text-ink-soft">{t('feedback.key.active', { hint: key.data.hint ?? '' })}</p>
      {secret ? <label className="block text-sm">{t('feedback.key.title')}<input className={fieldStyles} readOnly value={secret} autoComplete="off" spellCheck={false} /></label> : null}
      <div className="flex flex-wrap gap-2">
        <Button variant="secondary" busy={busy} onClick={() => secret ? setSecret('') : void submit(async () => { setSecret((await feedbackKeys.reveal(companyId)).secret); })}>{t(secret ? 'feedback.key.hide' : 'common.view')}</Button>
        <Button variant="secondary" busy={busy} onClick={() => void submit(async () => { const result = await feedbackKeys.reveal(companyId); await navigator.clipboard.writeText(result.secret); toast.success(t('common.copied')); })}>{t('common.copy')}</Button>
        <Button variant="ghost" disabled={busy} onClick={() => setRotating(true)}>{t('feedback.key.rotate')}</Button>
      </div>
      {rotating ? <div className="space-y-3 rounded border border-line p-3">
        <p className="text-sm">{t('feedback.key.rotateHint')}</p>
        <div className="flex gap-2"><Button busy={busy} onClick={() => void submit(async () => { setSecret(''); await feedbackKeys.rotate(companyId); await key.refetch(); setRotating(false); })}>{t('feedback.key.rotate')}</Button><Button variant="ghost" disabled={busy} onClick={() => setRotating(false)}>{t('common.cancel')}</Button></div>
      </div> : null}
    </> : key.data ? <Button busy={busy} onClick={() => void submit(async () => { await feedbackKeys.generate(companyId); await key.refetch(); })}>{t('feedback.key.generate')}</Button> : null}
  </section>;
}
